import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { Button } from './Button'
import { Input } from './Input'
import { Modal } from './Modal'
import { Select } from './Select'
import type { Option } from './types'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** als Frage mit Objekt: „Wareneingang WE-2026-0291 buchen?“ */
  title: string
  /** was passiert, in einem Satz */
  description?: ReactNode
  /** „42 Positionen“, „Lagerwert +9.412,80 €“, „Standort ZL“ */
  impact?: { label: string; value: ReactNode }[]
  tone?: 'neutral' | 'danger'
  /** wiederholt das Verb: „Buchen“, „Stornieren“ */
  confirmLabel: string
  cancelLabel?: string
  /** Storno- und Korrekturgrund */
  requireReason?: { label: string; options: Option[]; allowOther?: boolean }
  /** muss eingetippt werden, z. B. die Anzahl betroffener Datensätze */
  requireText?: string
  /** Promise → Schaltfläche lädt, Dialog bleibt bis zur Antwort; bei Fehler bleibt er offen und zeigt den Satz */
  onConfirm: (input: { reason?: string }) => void | Promise<void>
}

const OTHER = '__other__'

/** Bestätigung für alles, was festschreibt. Nie „Sind Sie sicher?“ – der Titel nennt Handlung und Objekt. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  impact,
  tone = 'neutral',
  confirmLabel,
  cancelLabel = 'Abbrechen',
  requireReason,
  requireText,
  onConfirm,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState<string | null>(null)
  const [otherReason, setOtherReason] = useState('')
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const cancelRef = useRef<HTMLButtonElement | null>(null)
  const confirmRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (open) {
      setReason(null)
      setOtherReason('')
      setTyped('')
      setBusy(false)
      setFailure(null)
    }
  }, [open])

  const reasonOptions: Option[] = requireReason
    ? [...requireReason.options, ...(requireReason.allowOther ? [{ value: OTHER, label: 'Anderer Grund …' }] : [])]
    : []
  const reasonText =
    reason === OTHER ? otherReason.trim() : (requireReason?.options.find((o) => o.value === reason)?.label ?? '')
  const reasonOk = !requireReason || (reason !== null && reasonText !== '')
  const typedOk = !requireText || typed.trim() === requireText
  const needsInput = Boolean(requireReason || requireText)

  const confirm = async (): Promise<void> => {
    if (!reasonOk || !typedOk || busy) return
    setFailure(null)
    try {
      const result = onConfirm(requireReason ? { reason: reasonText } : {})
      if (result instanceof Promise) {
        setBusy(true)
        await result
      }
      onOpenChange(false)
    } catch (error) {
      setFailure(
        error instanceof Error && error.message
          ? error.message
          : 'Die Aktion konnte nicht ausgeführt werden. Es wurde nichts geändert.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !busy && onOpenChange(next)}
      title={title}
      description={description}
      size="sm"
      dismissible={!busy}
      // Bei folgenreichen Aktionen liegt der Fokus auf „Abbrechen“, sonst auf der Bestätigung
      initialFocus={needsInput ? undefined : tone === 'danger' ? cancelRef : confirmRef}
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger-solid' : 'primary'}
            onClick={() => void confirm()}
            loading={busy}
            disabled={!reasonOk || !typedOk}
            disabledReason={
              !reasonOk ? 'Bitte einen Grund angeben' : !typedOk ? 'Bitte die Bestätigung eintippen' : undefined
            }
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {(impact || needsInput || failure) && (
        <div className="flex flex-col gap-4">
          {impact && impact.length > 0 && (
            <dl className="divide-y divide-line-subtle rounded-md border border-line bg-raised text-dense">
              {impact.map((entry, index) => (
                <div key={`${index}:${entry.label}`} className="flex items-baseline justify-between gap-4 px-3 py-2">
                  <dt className="min-w-0 shrink-0 basis-2/5 break-words text-fg-muted">{entry.label}</dt>
                  {/* lange Werte („1 Lieferschein → Rechnung über 216,00 €, fällig …“) brechen um statt abgeschnitten zu werden */}
                  <dd className="min-w-0 flex-1 text-right font-medium tabular-nums text-fg [overflow-wrap:anywhere]">{entry.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {requireReason && (
            <Select
              label={requireReason.label}
              value={reason}
              onChange={setReason}
              options={reasonOptions}
              native={false}
            />
          )}
          {requireReason && reason === OTHER && (
            <Input label="Grund" value={otherReason} onChange={setOtherReason} maxLength={200} autoFocus />
          )}
          {requireText && (
            <Input
              label={`Zur Bestätigung „${requireText}“ eintippen`}
              value={typed}
              onChange={setTyped}
              mono
              onKeyDown={(event) => {
                if (event.key === 'Enter') void confirm()
              }}
            />
          )}
          {failure && (
            <p
              role="alert"
              className="rounded-md border border-danger-line bg-danger-subtle px-3 py-2 text-dense text-danger-fg"
            >
              {failure}
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}

// ---- confirm(): Bestätigung ohne eigenen Zustand in der aufrufenden Komponente ----------------------------

export type ConfirmOptions = Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'onConfirm'>
export interface ConfirmResult {
  confirmed: boolean
  reason?: string
}

interface PendingConfirm {
  options: ConfirmOptions
  resolve: (result: ConfirmResult) => void
}

let pending: PendingConfirm | null = null
const hostListeners = new Set<() => void>()
const emit = (): void => hostListeners.forEach((listener) => listener())

/**
 * Fragt nach und liefert die Entscheidung. Braucht <UiProvider> (bzw. <ConfirmHost>) in der Anwendung.
 * Nie zwei Dialoge übereinander: Eine zweite Anfrage beantwortet die erste mit „nicht bestätigt“.
 */
export function confirm(options: ConfirmOptions): Promise<ConfirmResult> {
  pending?.resolve({ confirmed: false })
  return new Promise((resolve) => {
    pending = { options, resolve }
    emit()
  })
}

export function ConfirmHost() {
  const current = useSyncExternalStore(
    (listener) => {
      hostListeners.add(listener)
      return () => hostListeners.delete(listener)
    },
    () => pending,
    () => null,
  )
  const [open, setOpen] = useState(false)
  const shown = useRef<PendingConfirm | null>(null)
  if (current && current !== shown.current) shown.current = current

  useEffect(() => {
    if (current) setOpen(true)
  }, [current])

  const settle = (result: ConfirmResult): void => {
    const request = shown.current
    if (request && pending === request) {
      pending = null
      request.resolve(result)
      emit()
    }
    setOpen(false)
  }

  const options = shown.current?.options
  if (!options) return null
  return (
    <ConfirmDialog
      {...options}
      open={open}
      onOpenChange={(next) => {
        if (!next) settle({ confirmed: false })
      }}
      onConfirm={(input) => settle({ confirmed: true, ...input })}
    />
  )
}
